import { 
  Component,
  OnInit,
  Input,
  ChangeDetectionStrategy,
  AfterViewInit,
  Renderer2,
  ChangeDetectorRef,
  OnDestroy,
  ElementRef, 
  ViewEncapsulation,
  Output,
  EventEmitter} from '@angular/core';
import { BdGraph } from 'src/lib/graph/graph.model';
import { ViewTreeNode } from 'src/app/engine/models/view.tree-node';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TreeTheme } from '../tree/tree.component';
import { EngineService } from 'src/app/engine/engine.service';
import { Store } from '@ngxs/store';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { remove as removeDiacritics } from 'diacritics';
import { getSearchTree } from './tree-search';


export enum TreeSearchDisplayMode {
  List,
  Tree
}

@Component({
  selector: 'app-tree-search',
  templateUrl: './tree-search.component.html',
  styleUrls: ['./tree-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class TreeSearchComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  public get query() {
    return this._query;
  }
  public set query(value) {
    this._query = value || '';
    this.handleQueryChange();
  }
  private _query = '';

  @Input()
  public get displayMode() {
    return this._displayMode;
  }
  public set displayMode(value) {
    if (value == null) {
      return;
    }

    this._displayMode = value;
    this.handleQueryChange();

    this.displayModeChange.emit(this.displayMode);
  }
  _displayMode = TreeSearchDisplayMode.List;
  
  @Output()
  displayModeChange = new EventEmitter<TreeSearchDisplayMode>();

  @Input()
  public get viewGraph(): BdGraph<ViewTreeNode> {
    return this._viewGraph;
  }
  public set viewGraph(value: BdGraph<ViewTreeNode>) {
    this._viewGraph = value;
    this.leafNodes = this.engineService.getLeaves();

    // create a pruned version of the viewGraph for interested consumers.
    this.buildPrunedViewGraph(this.displayMode);
    
    this.handleQueryChange();
    this.changeDetectorRef.markForCheck();
  }
  private _viewGraph: BdGraph<ViewTreeNode>;

  @Input()
  theme: TreeTheme = TreeTheme.Dark;

  treeSearchDisplayMode = TreeSearchDisplayMode;
  searchResults: ViewTreeNode[] = [];
  searchHeight = 0;
  ngUnsubscribe = new Subject();
  leafNodes: string[];
  searchGraph: BdGraph<ViewTreeNode>;
  prunedViewGraph: BdGraph<ViewTreeNode>;

  constructor(
    private renderer: Renderer2,
    private changeDetectorRef: ChangeDetectorRef,
    private elementRef: ElementRef,
    private engineService: EngineService,
    private store: Store
  ) { }

  ngOnInit() {
  }

  buildPrunedViewGraph(mode: TreeSearchDisplayMode) {
    // prune the graph according to chapter show objects
    const currentChapter = this.store.selectSnapshot(StoryChaptersState.getSelectedChapter);

    const prunedViewGraph: BdGraph<ViewTreeNode> = this.engineService.createViewGraph(
      currentChapter.showObjects,
      true // always prune on view page
    );

    this.prunedViewGraph = prunedViewGraph;
  }

  ngAfterViewInit() {
    // Window has resized, let the 3D canvas know
    this.renderer.listen('window', 'resize', (event) => {
      this.resize();
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  handleQueryChange() {
    const searchResults = this.search(
      this._query,
      this.displayMode === TreeSearchDisplayMode.List
        ? this.prunedViewGraph
        : this.viewGraph
    );


    if (this.displayMode === TreeSearchDisplayMode.Tree) {
      this.searchGraph = getSearchTree(this.viewGraph, searchResults);
    }

    this.searchResults = searchResults;
    this.resize();
  }

  resize() {
    this.getSearchHeight()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((height) => {
        this.searchHeight = height;
        this.changeDetectorRef.markForCheck();
      });
  }

  getSearchHeight(): Observable<number> {
    const e = this.elementRef;

    return new Observable<number>((observer) => {
      setTimeout(() => {
        const offsetHeight = this.elementRef.nativeElement.offsetHeight;
  
        if (offsetHeight != null) {
          observer.next(offsetHeight);
          observer.complete();
        }
      });
    });
  }

  // return a sorted array of leaf nodes matching a query
  search(query: string, viewGraph: BdGraph<ViewTreeNode>): ViewTreeNode[] {
    if (viewGraph === undefined) {  
      return [];
    }

    query = removeDiacritics(query.toLowerCase());
    
    const results = Object.values(viewGraph.nodes)
      .filter((node: ViewTreeNode) => {
        if (query.length === 0) {
          return true;
        }

        return removeDiacritics(node.label.toLowerCase())
          .indexOf(query) >= 0;
      })
      .sort((a, b) => {
        return a.label.localeCompare(b.label);
      });

      return results;
  }

  trackTreeNode(index: number, node: ViewTreeNode) { 
    return node.id; 
  }
  
}
